import { useSelector } from "react-redux";
import { useXmlConfiguratorTranslations } from "~/Configurators/common/useXmlConfiguratorTranslations";
import {
  renderValue,
  SummaryProviderContextProvider,
  Table,
  useSummaryProvider,
} from "~/Configurators/Shaft/inch/components/ShaftSummaryDataHelper";
import classes from "~/Configurators/Shaft/inch/components/InchShaftSummaryData.module.less";
import {
  OnStepChangeContextProvider,
  ShaftSummaryStepFrame,
} from "~/Configurators/Shaft/components/ShaftSummaryStepFrame";
import type { RootState } from "~/redux/store";
import { getHighestVisitedStep, selectBaseDesignation } from "~/Configurators/Linear/redux/selectors";
import { SummaryDataProvider } from "~/Configurators/Shaft/SummaryDataProvider";
import {
  getStep1SummaryData,
  getStep2SummaryData,
  getStep2SummaryDataRail,
  getStep3SummaryDataCarriage,
} from "./KLLTUnitSummaryDataProvider";
import { Box, Stack, Typography } from "@mui/material";
import { t } from "i18next";

interface Props {
  isFullReview: boolean;
  onStepChange?: () => void;
}

export const SummaryData = ({ onStepChange, isFullReview }: Props) => {
  const xmlT = useXmlConfiguratorTranslations().t!;

  const summaryProvider = useSelector((state: RootState) => {
    return new SummaryDataProvider(state.linearConfigurator, xmlT);
  });

  const highestVisitedStep = useSelector(getHighestVisitedStep);

  return (
    <OnStepChangeContextProvider value={onStepChange}>
      <SummaryProviderContextProvider value={summaryProvider}>
        <div className={classes.summaryStepContainer}>
          {highestVisitedStep >= 0 && <Step1Summary isFullReview={isFullReview} />}
          {highestVisitedStep >= 1 && <Step2Summary />}
          {highestVisitedStep >= 2 && <Step3Summary />}
        </div>
      </SummaryProviderContextProvider>
    </OnStepChangeContextProvider>
  );
};

const Step1Summary = ({ isFullReview }: { isFullReview: boolean }) => {
  const xmlT = useXmlConfiguratorTranslations().t!;
  const summaryProvider = useSummaryProvider();

  const step1Data = getStep1SummaryData(summaryProvider) ?? [];
  const designation = useSelector(selectBaseDesignation);
  const productImageUrl = useSelector(selectLCImage);

  return (
    <ShaftSummaryStepFrame stepNumber={1} name={xmlT("configurator.unit.page.1.name")}>
      <Stack gap={1}>
        <Typography variant={"bodyLarge"} sx={{ fontWeight: "bold" }}>
          {designation}
        </Typography>
        <Table>
          {step1Data.map((data) => (
            <tbody key={data.name?.toString()}>
              <tr>
                <td colSpan={2}>
                  <div>{data.name}</div>
                </td>
                <td colSpan={2}>
                  <div>{renderValue(data)}</div>
                </td>
                <td colSpan={2} />
              </tr>
            </tbody>
          ))}
        </Table>
      </Stack>
      {!isFullReview && (
        <Box sx={{ mt: 2 }}>
          <img src={xmlT(productImageUrl)} alt="linear configuration product" />
        </Box>
      )}
    </ShaftSummaryStepFrame>
  );
};

const Step2Summary = () => {
  const xmlT = useXmlConfiguratorTranslations().t!;
  const summaryProvider = useSummaryProvider();

  const step2Data = getStep2SummaryData(summaryProvider) ?? [];

  const guidewayDetailsData = getStep2SummaryDataRail(summaryProvider) ?? [];

  const guidewayAmount = guidewayDetailsData[0]?.rail.length ?? 0;
  const guidewayList = new Array(guidewayAmount).fill(0);
  const fillerList = new Array(2 - guidewayAmount).fill(0);

  return (
    <ShaftSummaryStepFrame stepNumber={2} name={xmlT("configurator.unit.page.2.name")}>
      <Stack gap={1}>
        <Table>
          {step2Data.map((data) => (
            <tbody key={data.name?.toString()}>
              <tr>
                <td colSpan={2}>
                  <div dangerouslySetInnerHTML={{ __html: data.name }} />
                </td>
                <td colSpan={2}>
                  <div>{renderValue(data)}</div>
                </td>
                <td colSpan={2} />
              </tr>
            </tbody>
          ))}
        </Table>
        {guidewayDetailsData && (
          <Table>
            <thead>
              <tr>
                <th colSpan={2}></th>
                {guidewayList.map((_, index) => (
                  <th key={index} colSpan={2}>
                    {t("linearConfigSummary.step.headerGuidewaySet", { count: index + 1 })}
                  </th>
                ))}
                {fillerList.map((_, index) => (
                  <th key={index} colSpan={2}></th>
                ))}
              </tr>
            </thead>
            {guidewayDetailsData.map((data) => (
              <tbody key={data.name?.toString()}>
                <tr>
                  <td colSpan={2}>
                    <div dangerouslySetInnerHTML={{ __html: data.name }} />
                  </td>
                  {data.rail.map((value) => (
                    <td colSpan={2}>
                      <div>{renderValue(value)}</div>
                    </td>
                  ))}

                  <td colSpan={2} />
                </tr>
              </tbody>
            ))}
          </Table>
        )}
      </Stack>
    </ShaftSummaryStepFrame>
  );
};

const Step3Summary = () => {
  const xmlT = useXmlConfiguratorTranslations().t!;
  const summaryProvider = useSummaryProvider();

  const carriageDetailsData = getStep3SummaryDataCarriage(summaryProvider) ?? [];

  const railAmount = carriageDetailsData[0]?.rail.length ?? 0;
  const carriageAmount = carriageDetailsData[0]?.rail[0]?.carriage.length ?? 0;

  if (!railAmount || !carriageAmount) {
    return null;
  }

  return (
    <ShaftSummaryStepFrame stepNumber={3} name={xmlT("configurator.unit.page.3.name")}>
      <Stack gap={1}>
        {carriageDetailsData &&
          Array.from({ length: railAmount }, (_, railIndex) => (
            <Table key={railIndex} colAmount={2 + carriageAmount * 2}>
              <thead>
                <tr>
                  <th colSpan={2}></th>
                  {Array.from({ length: carriageAmount }, (_, carriageIndex) => (
                    <th key={carriageIndex} colSpan={2}>
                      {t("linearConfigSummary.step7.carriage", { count: railIndex + 1 + (carriageIndex + 1) / 10 })}
                    </th>
                  ))}
                </tr>
              </thead>
              {carriageDetailsData.map((data) => (
                <tbody key={data.name?.toString()}>
                  <tr>
                    <td colSpan={2}>
                      <div dangerouslySetInnerHTML={{ __html: data.name }} />
                    </td>
                    {data.rail[railIndex].carriage.map((value) => (
                      <td colSpan={2}>
                        <div>{renderValue(value)}</div>
                      </td>
                    ))}
                  </tr>
                </tbody>
              ))}
            </Table>
          ))}
      </Stack>
    </ShaftSummaryStepFrame>
  );
};

const selectLCImage = (state: RootState) => state.linearConfigurator.productImageUrl;
